import { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { formatCurrency, formatDateShort } from '../utils/format';
import { Plus, Check, X, Trash2, AlertCircle, FileText, Download, Search, RefreshCw, RotateCcw, Calendar, Filter, Image, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import FileUpload from '../components/FileUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AYLAR = [
  { value: '1', label: 'Ocak' },
  { value: '2', label: 'Şubat' },
  { value: '3', label: 'Mart' },
  { value: '4', label: 'Nisan' },
  { value: '5', label: 'Mayıs' },
  { value: '6', label: 'Haziran' },
  { value: '7', label: 'Temmuz' },
  { value: '8', label: 'Ağustos' },
  { value: '9', label: 'Eylül' },
  { value: '10', label: 'Ekim' },
  { value: '11', label: 'Kasım' },
  { value: '12', label: 'Aralık' }
];

const currentYear = new Date().getFullYear();
const YILLAR = Array.from({ length: 5 }, (_, i) => ({
  value: String(currentYear - 2 + i),
  label: String(currentYear - 2 + i)
}));

export default function Cekler() {
  const { currentUser, kullanicilar } = useApp();
  const [cekler, setCekler] = useState([]);
  const [ozet, setOzet] = useState(null);
  const [vadesiYaklaşanlar, setVadesiYaklaşanlar] = useState([]);
  const [bankalar, setBankalar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  
  // Filters
  const [filter, setFilter] = useState('hepsi');
  const [selectedAy, setSelectedAy] = useState('tumu');
  const [selectedYil, setSelectedYil] = useState(String(currentYear));
  const [selectedBanka, setSelectedBanka] = useState('tumu');
  const [baslangicTarihi, setBaslangicTarihi] = useState('');
  const [bitisTarihi, setBitisTarihi] = useState('');
  const [arama, setArama] = useState('');
  const [showSilinenler, setShowSilinenler] = useState(false);
  
  // Görsel yükleme dialog
  const [showGorselDialog, setShowGorselDialog] = useState(false);
  const [selectedCek, setSelectedCek] = useState(null);
  
  const [addForm, setAddForm] = useState({
    cekNo: '',
    verilen: '',
    bankaId: '',
    tutar: '',
    vadeTarihi: '',
    not_: ''
  });

  useEffect(() => {
    loadBankalar();
  }, []);

  useEffect(() => {
    loadCekler();
  }, [filter, selectedAy, selectedYil, selectedBanka, baslangicTarihi, bitisTarihi, arama, showSilinenler]);

  const loadBankalar = async () => {
    try {
      const res = await api.getBankalar();
      // Deduplicate banks by name - each bank exists as both kartPos and havale
      // For checks, we only need unique bank names
      const uniqueBanks = [];
      const seenNames = new Set();
      for (const banka of res.data) {
        if (!seenNames.has(banka.ad)) {
          seenNames.add(banka.ad);
          uniqueBanks.push(banka);
        }
      }
      setBankalar(uniqueBanks);
    } catch (error) {
      console.error('Error loading banks:', error);
    }
  };

  const getKullaniciKisaAd = (kullaniciId) => {
    if (!kullaniciId || !kullanicilar) return '';
    const kullanici = kullanicilar.find(k => k.id === kullaniciId);
    if (!kullanici) return '';
    const parcalar = kullanici.ad.split(' ');
    if (parcalar.length >= 2) {
      return `${parcalar[0][0]}${parcalar[1][0]}`.toUpperCase();
    }
    return parcalar[0]?.substring(0, 2).toUpperCase() || '';
  };

  const loadCekler = async () => {
    try {
      setLoading(true);
      const params = {
        subeId: currentUser.rol === 'sube_sorumlusu' ? currentUser.subeId : undefined,
        kullaniciId: currentUser.rol === 'veri_giris' ? currentUser.id : undefined,
        rol: currentUser.rol,
        durum: filter === 'hepsi' ? undefined : filter,
        ay: selectedAy !== 'tumu' ? parseInt(selectedAy) : undefined,
        yil: selectedYil ? parseInt(selectedYil) : undefined,
        bankaId: selectedBanka !== 'tumu' ? selectedBanka : undefined,
        baslangicTarihi: baslangicTarihi || undefined,
        bitisTarihi: bitisTarihi || undefined,
        arama: arama || undefined,
        silinmisler: showSilinenler
      };

      const [ceklerRes, ozetRes, vadesiRes] = await Promise.all([
        api.getCekler(params),
        api.getCeklerOzet({
          ay: params.ay,
          yil: params.yil,
          bankaId: params.bankaId,
          baslangicTarihi: params.baslangicTarihi,
          bitisTarihi: params.bitisTarihi
        }),
        api.getVadesiYaklasanCekler(30)
      ]);

      setCekler(ceklerRes.data);
      setOzet(ozetRes.data);
      setVadesiYaklaşanlar(vadesiRes.data);
    } catch (error) {
      console.error('Error loading cekler:', error);
      toast.error('Çekler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadCekler();
    toast.success('Veriler yenilendi');
  };

  const handleAdd = async () => {
    if (!addForm.cekNo || !addForm.verilen || !addForm.tutar || !addForm.vadeTarihi) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    try {
      await api.createCek({
        cekNo: addForm.cekNo,
        verilen: addForm.verilen,
        bankaId: addForm.bankaId || undefined,
        tutar: parseFloat(addForm.tutar),
        vadeTarihi: addForm.vadeTarihi,
        subeId: currentUser.subeId,
        girenKullaniciId: currentUser.id,
        not_: addForm.not_
      });
      
      toast.success('Çek eklendi');
      setShowAddDialog(false);
      setAddForm({ cekNo: '', verilen: '', bankaId: '', tutar: '', vadeTarihi: '', not_: '' });
      loadCekler();
    } catch (error) {
      console.error('Error adding cek:', error);
      toast.error('Çek eklenirken hata oluştu');
    }
  };

  const handleTahsil = async (cekId) => {
    try {
      await api.tahsilEtCek(cekId);
      toast.success('Çek ödenmiş olarak işaretlendi');
      loadCekler();
    } catch (error) {
      console.error('Error tahsil:', error);
      toast.error('İşlem başarısız');
    }
  };

  const handleIade = async (cekId) => {
    try {
      await api.iadeEtCek(cekId);
      toast.success('Çek beklemede olarak işaretlendi');
      loadCekler();
    } catch (error) {
      console.error('Error iade:', error);
      toast.error('İşlem başarısız');
    }
  };

  const handleDelete = async (cekId) => {
    if (!window.confirm('Bu çeki silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      await api.deleteCek(cekId, currentUser.id);
      toast.success('Çek silindi');
      loadCekler();
    } catch (error) {
      console.error('Error deleting cek:', error);
      toast.error('Çek silinirken hata oluştu');
    }
  };

  const handleGeriYukle = async (cekId) => {
    try {
      await api.geriYukleCek(cekId);
      toast.success('Çek geri yüklendi');
      loadCekler();
    } catch (error) {
      console.error('Error restoring cek:', error);
      toast.error('Geri yükleme başarısız');
    }
  };

  const handleExcelExport = () => {
    // Filtre parametrelerini URL'e ekle
    const params = new URLSearchParams();
    
    if (selectedAy !== 'tumu') params.append('ay', selectedAy);
    if (selectedYil !== 'tumu') params.append('yil', selectedYil);
    if (baslangicTarihi) params.append('baslangicTarihi', baslangicTarihi);
    if (bitisTarihi) params.append('bitisTarihi', bitisTarihi);
    if (selectedBanka !== 'tumu') params.append('bankaId', selectedBanka);
    if (filter !== 'hepsi') params.append('durum', filter);
    if (arama) params.append('arama', arama);
    
    const queryString = params.toString();
    const url = `${BACKEND_URL}/api/export/cekler/excel${queryString ? '?' + queryString : ''}`;
    window.open(url, '_blank');
  };

  const handlePdfExport = () => {
    // Filtre parametrelerini URL'e ekle
    const params = new URLSearchParams();
    
    if (selectedAy !== 'tumu') params.append('ay', selectedAy);
    if (selectedYil !== 'tumu') params.append('yil', selectedYil);
    if (baslangicTarihi) params.append('baslangicTarihi', baslangicTarihi);
    if (bitisTarihi) params.append('bitisTarihi', bitisTarihi);
    if (selectedBanka !== 'tumu') params.append('bankaId', selectedBanka);
    if (filter !== 'hepsi') params.append('durum', filter);
    if (arama) params.append('arama', arama);
    
    const queryString = params.toString();
    const url = `${BACKEND_URL}/api/export/cekler/pdf${queryString ? '?' + queryString : ''}`;
    window.open(url, '_blank');
  };

  const handleOpenGorselDialog = (cek) => {
    setSelectedCek(cek);
    setShowGorselDialog(true);
  };

  const handleGorselUploadSuccess = (url) => {
    setSelectedCek(prev => ({ ...prev, gorselUrl: url }));
    loadCekler();
  };

  const handleGorselDeleteSuccess = () => {
    setSelectedCek(prev => ({ ...prev, gorselUrl: null }));
    loadCekler();
  };

  const clearFilters = () => {
    setFilter('hepsi');
    setSelectedAy('tumu');
    setSelectedYil(String(currentYear));
    setSelectedBanka('tumu');
    setBaslangicTarihi('');
    setBitisTarihi('');
    setArama('');
  };

  const getDurumBadge = (durum) => {
    const styles = {
      beklemede: 'bg-amber-100 text-amber-800 border border-amber-300',
      odendi: 'bg-success/10 text-success border border-success'
    };
    const labels = { beklemede: 'Beklemede', odendi: 'Ödendi' };
    return (
      <span className={`px-2 py-1 text-xs font-medium ${styles[durum]}`}>
        {labels[durum]}
      </span>
    );
  };

  const getBankaAdi = (bankaId) => {
    if (!bankaId) return '-';
    const banka = bankalar.find(b => b.id === bankaId);
    return banka?.ad || '-';
  };

  if (loading && cekler.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textSecondary">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6" data-testid="cekler-page">
      <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
        <div className="flex items-center gap-2 md:gap-3">
          <div>
            <h1 className="text-xl md:text-3xl font-heading font-bold text-textPrimary">Verilen Çekler</h1>
            <p className="text-xs md:text-sm text-textSecondary mt-1">Ödeme çeklerini takip edin</p>
          </div>
          <button
            onClick={handleRefresh}
            className="p-2 text-textSecondary hover:text-primary hover:bg-surfaceSubtle rounded-full transition-colors"
            title="Yenile"
            data-testid="refresh-btn"
          >
            <RefreshCw className="h-4 w-4 md:h-5 md:w-5" />
          </button>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
          <Button
            onClick={handleExcelExport}
            variant="outline"
            size="sm"
            className="flex items-center gap-1 md:gap-2 whitespace-nowrap flex-shrink-0"
            data-testid="export-excel-btn"
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">Excel</span>
          </Button>
          <Button
            onClick={handlePdfExport}
            variant="outline"
            size="sm"
            className="flex items-center gap-1 md:gap-2 whitespace-nowrap flex-shrink-0"
            data-testid="export-pdf-btn"
          >
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">PDF</span>
          </Button>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-cek-btn"
                size="sm"
                className="bg-primary hover:bg-primaryHover text-primaryForeground whitespace-nowrap flex-shrink-0"
              >
                <Plus className="h-4 w-4 md:h-5 md:w-5 mr-1 md:mr-2" />
                <span className="hidden sm:inline">Yeni Çek Ekle</span>
                <span className="sm:hidden">Ekle</span>
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]" data-testid="add-cek-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading">Yeni Çek Ekle</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Çek No *</label>
                  <Input
                    data-testid="cek-no-input"
                    value={addForm.cekNo}
                    onChange={(e) => setAddForm({ ...addForm, cekNo: e.target.value })}
                    placeholder="Örn: 123456"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Verilen (Kime) *</label>
                  <Input
                    data-testid="verilen-input"
                    value={addForm.verilen}
                    onChange={(e) => setAddForm({ ...addForm, verilen: e.target.value })}
                    placeholder="Çekin verildiği kişi/firma"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Banka</label>
                  <Select value={addForm.bankaId} onValueChange={(value) => setAddForm({ ...addForm, bankaId: value })}>
                    <SelectTrigger data-testid="banka-select" className="w-full">
                      <SelectValue placeholder="Banka seçin (opsiyonel)" />
                    </SelectTrigger>
                    <SelectContent>
                      {bankalar.map((banka) => (
                        <SelectItem key={banka.id} value={banka.id}>{banka.ad}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Tutar (₺) *</label>
                  <Input
                    type="number"
                    step="0.01"
                    data-testid="tutar-input"
                    value={addForm.tutar}
                    onChange={(e) => setAddForm({ ...addForm, tutar: e.target.value })}
                    placeholder="0.00"
                    className="font-mono"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Vade Tarihi *</label>
                  <Input
                    type="date"
                    data-testid="vade-tarihi-input"
                    value={addForm.vadeTarihi}
                    onChange={(e) => setAddForm({ ...addForm, vadeTarihi: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-textPrimary mb-2">Not (Opsiyonel)</label>
                  <Textarea
                    data-testid="not-input"
                    value={addForm.not_}
                    onChange={(e) => setAddForm({ ...addForm, not_: e.target.value })}
                    placeholder="Ek notlar..."
                    className="min-h-[60px]"
                  />
                </div>
                <Button
                  onClick={handleAdd}
                  data-testid="submit-cek-btn"
                  className="w-full bg-primary hover:bg-primaryHover text-primaryForeground"
                >
                  Kaydet
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Özet Kartları */}
      {ozet && !showSilinenler && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-surface border border-border p-6">
            <p className="text-sm text-textSecondary mb-1">Bekleyen Çekler</p>
            <p className="text-2xl font-mono font-bold text-amber-600">{formatCurrency(ozet.bekleyenToplam)}</p>
            <p className="text-xs text-textSecondary mt-1">{ozet.bekleyenAdet} adet</p>
          </div>
          <div className="bg-surface border border-border p-6">
            <p className="text-sm text-textSecondary mb-1">Ödenen Çekler</p>
            <p className="text-2xl font-mono font-bold text-success">{formatCurrency(ozet.odenmisToplam)}</p>
            <p className="text-xs text-textSecondary mt-1">{ozet.odenmisAdet} adet</p>
          </div>
          <div className="bg-surface border border-border p-6">
            <p className="text-sm text-textSecondary mb-1">Toplam</p>
            <p className="text-2xl font-mono font-bold text-textPrimary">{formatCurrency(ozet.genelToplam)}</p>
            <p className="text-xs text-textSecondary mt-1">{ozet.genelAdet} adet</p>
          </div>
        </div>
      )}

      {/* Filtreler */}
      <div className="bg-surface border border-border p-3 md:p-4">
        <div className="flex flex-col gap-3">
          {/* Üst satır - Arama ve tarihler */}
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 md:h-5 md:w-5 text-textSecondary hidden sm:block" />
            
            {/* Arama */}
            <div className="relative flex-1 min-w-[120px] max-w-[200px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-textSecondary" />
              <Input
                type="text"
                value={arama}
                onChange={(e) => setArama(e.target.value)}
                placeholder="Ara..."
                className="pl-8 h-9 text-sm"
                data-testid="arama-input"
              />
            </div>

            {/* Tarih Aralığı */}
            <div className="flex items-center gap-1 md:gap-2">
              <Calendar className="h-4 w-4 text-textSecondary hidden md:block" />
              <Input
                type="date"
                value={baslangicTarihi}
                onChange={(e) => setBaslangicTarihi(e.target.value)}
                className="w-[110px] md:w-[130px] h-9 text-sm"
              />
              <span className="text-textSecondary">-</span>
              <Input
                type="date"
                value={bitisTarihi}
                onChange={(e) => setBitisTarihi(e.target.value)}
                className="w-[110px] md:w-[130px] h-9 text-sm"
              />
            </div>
          </div>
          
          {/* Alt satır - Selectler */}
          <div className="flex flex-wrap items-center gap-2">

          {/* Ay */}
          <Select value={selectedAy} onValueChange={setSelectedAy}>
            <SelectTrigger className="w-[80px] md:w-[100px] h-9 text-sm">
              <SelectValue placeholder="Ay" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tumu">Tüm Aylar</SelectItem>
              {AYLAR.map((ay) => (
                <SelectItem key={ay.value} value={ay.value}>{ay.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Yıl */}
          <Select value={selectedYil} onValueChange={setSelectedYil}>
            <SelectTrigger className="w-[70px] md:w-[90px] h-9 text-sm">
              <SelectValue placeholder="Yıl" />
            </SelectTrigger>
            <SelectContent>
              {YILLAR.map((yil) => (
                <SelectItem key={yil.value} value={yil.value}>{yil.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Banka */}
          <Select value={selectedBanka} onValueChange={setSelectedBanka}>
            <SelectTrigger className="w-[90px] md:w-[120px] h-9 text-sm">
              <SelectValue placeholder="Banka" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tumu">Tüm Bankalar</SelectItem>
              {bankalar.map((banka) => (
                <SelectItem key={banka.id} value={banka.id}>{banka.ad}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Durum */}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[80px] md:w-[110px] h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hepsi">Hepsi</SelectItem>
              <SelectItem value="beklemede">Beklemede</SelectItem>
              <SelectItem value="odendi">Ödendi</SelectItem>
            </SelectContent>
          </Select>

          {/* Temizle */}
          <Button
            onClick={clearFilters}
            variant="ghost"
            size="sm"
            className="text-textSecondary text-xs"
          >
            <X className="h-4 w-4" />
            <span className="hidden sm:inline ml-1">Temizle</span>
          </Button>
          </div>
        </div>

        {/* Sekme: Normal / Silinenler */}
        <div className="flex gap-2 mt-3 pt-3 border-t border-border">
          <button
            onClick={() => setShowSilinenler(false)}
            className={`px-2 md:px-3 py-1.5 text-xs md:text-sm font-medium rounded transition-colors ${
              !showSilinenler ? 'bg-primary text-primaryForeground' : 'text-textSecondary hover:bg-surfaceSubtle'
            }`}
          >
            Çekler
          </button>
          <button
            onClick={() => setShowSilinenler(true)}
            className={`px-2 md:px-3 py-1.5 text-xs md:text-sm font-medium rounded transition-colors ${
              showSilinenler ? 'bg-error text-white' : 'text-textSecondary hover:bg-surfaceSubtle'
            }`}
          >
            Silinenler
          </button>
        </div>
      </div>

      {/* Vadesi Yaklaşan Uyarı */}
      {vadesiYaklaşanlar.length > 0 && !showSilinenler && (
        <div className="bg-amber-50 border border-amber-300 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-amber-900">
                30 Gün İçinde Vadesi Dolan {vadesiYaklaşanlar.length} Çek
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Toplam: {formatCurrency(vadesiYaklaşanlar.reduce((sum, c) => sum + c.tutar, 0))}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Çek Listesi */}
      {cekler.length === 0 ? (
        <div className="bg-surface border border-border p-12 text-center">
          <FileText className="h-16 w-16 text-textSecondary mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-textSecondary">
            {showSilinenler ? 'Silinmiş çek bulunamadı' : 'Çek bulunamadı'}
          </p>
        </div>
      ) : (
        <div className="bg-surface border border-border">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="cekler-table">
              <thead className="bg-surfaceSubtle border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">Çek No</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">Verilen</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">Banka</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-textSecondary uppercase">Tutar</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">Vade</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">Durum</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-textSecondary uppercase">Görsel</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-textSecondary uppercase">
                    {showSilinenler ? 'Silen' : 'Giren'}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textSecondary uppercase">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {cekler.map((cek, index) => (
                  <tr key={cek.id} data-testid={`cek-row-${index}`} className="hover:bg-surfaceSubtle/30">
                    <td className="px-4 py-3 text-sm font-medium text-textPrimary">{cek.cekNo}</td>
                    <td className="px-4 py-3 text-sm text-textPrimary">{cek.verilen}</td>
                    <td className="px-4 py-3 text-sm text-textSecondary">{getBankaAdi(cek.bankaId)}</td>
                    <td className="px-4 py-3 text-sm font-mono text-right font-bold text-textPrimary">
                      {formatCurrency(cek.tutar)}
                    </td>
                    <td className="px-4 py-3 text-sm text-textPrimary">{formatDateShort(cek.vadeTarihi)}</td>
                    <td className="px-4 py-3">{getDurumBadge(cek.durum)}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleOpenGorselDialog(cek)}
                        className={`p-1.5 rounded transition-colors ${
                          cek.gorselUrl 
                            ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' 
                            : 'text-textSecondary hover:bg-surfaceSubtle'
                        }`}
                        title={cek.gorselUrl ? 'Görseli Görüntüle' : 'Görsel Ekle'}
                      >
                        {cek.gorselUrl ? <Eye className="h-4 w-4" /> : <Image className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-xs font-medium text-textSecondary bg-surfaceSubtle px-1.5 py-0.5 rounded">
                        {getKullaniciKisaAd(showSilinenler ? cek.silenKullaniciId : cek.girenKullaniciId)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {showSilinenler ? (
                          <button
                            onClick={() => handleGeriYukle(cek.id)}
                            className="text-primary hover:text-primaryHover"
                            title="Geri Yükle"
                          >
                            <RotateCcw className="h-4 w-4" />
                          </button>
                        ) : (
                          <>
                            {cek.durum === 'beklemede' && (
                              <button
                                onClick={() => handleTahsil(cek.id)}
                                className="text-success hover:text-success/80"
                                title="Ödendi"
                              >
                                <Check className="h-5 w-5" />
                              </button>
                            )}
                            {cek.durum === 'odendi' && currentUser?.rol === 'admin' && (
                              <button
                                onClick={() => handleIade(cek.id)}
                                className="text-amber-600 hover:text-amber-700"
                                title="Beklemede'ye döndür"
                              >
                                <X className="h-5 w-5" />
                              </button>
                            )}
                            {currentUser?.rol === 'admin' && (
                              <button
                                onClick={() => handleDelete(cek.id)}
                                className="text-error hover:text-error/80"
                                title="Sil"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Çek Görseli Dialog */}
      <Dialog open={showGorselDialog} onOpenChange={setShowGorselDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Çek Görseli</DialogTitle>
            <DialogDescription>
              {selectedCek?.cekNo} numaralı çek için görsel yükleyin veya görüntüleyin
            </DialogDescription>
          </DialogHeader>
          {selectedCek && (
            <div className="py-4">
              <div className="mb-4 p-3 bg-surfaceSubtle rounded-lg">
                <p className="text-sm"><strong>Çek No:</strong> {selectedCek.cekNo}</p>
                <p className="text-sm"><strong>Verilen:</strong> {selectedCek.verilen}</p>
                <p className="text-sm"><strong>Tutar:</strong> {formatCurrency(selectedCek.tutar)}</p>
              </div>
              <FileUpload
                endpoint={`/api/upload/cek/${selectedCek.id}`}
                currentUrl={selectedCek.gorselUrl}
                onUploadSuccess={handleGorselUploadSuccess}
                onDeleteSuccess={handleGorselDeleteSuccess}
                label="Çek Görseli Yükle"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
